import {
  CARD_HOLD_AMOUNT_MAX_CENTS,
  CARD_HOLD_AMOUNT_MIN_CENTS,
  CARD_HOLD_WALKOUT_GRATUITY_MAX_PERCENT,
  CARD_HOLD_WALKOUT_GRATUITY_MIN_PERCENT,
  CUSTOM_PAGE_SLUG_MAX_LENGTH,
  findDuplicateCustomPageSlugs,
  OFFLINE_PAYMENT_SETTINGS_BOUNDS,
  validateCustomPageSlug,
} from "@order-up/models";
import { z } from "zod";
import { GuestWifiSettingsSchema } from "../guest_codes/GuestCode";
import { GuestOrderingSettingsSchema } from "../guest_ordering/GuestOrderingSettings";
import {
  MenuSyncPriceAdjustmentSchema,
  MenuSyncPriceAdjustmentsSchema,
} from "../integrations/Integration";
import { PosConfigSchema } from "../orders/PosConfig";
import { LocationPrintingSettingsSchema } from "../kitchen/PrinterSettings";
import { UpdateBookingSettingsSchema } from "../appointments/BookingSettings";
import { LocationBarSpiritListsBlockSchema } from "../bar/BarSpiritList";
import { LocationBarWineListBlockSchema } from "../bar/BarWineList";
import {
  PaymentModeSchema,
  PaymentRoutingSettingsSchema,
} from "../payments/PaymentRouting";
import { LocationAlertConfigSchema, LocationRemoteApprovalConfigSchema, LocationShiftAlertsConfigSchema } from "../locations/LocationAlerts"; // scoped-path-ok: module path
import { OrderAlertRoutingConfigSchema } from "../locations/OrderAlertRouting"; // scoped-path-ok: module path

// Base address schema
const AddressSchema = z
  .object({
    street: z
      .string()
      .min(1, { message: "Street address is required" })
      .readonly(),
    street2: z.string().min(1).optional().readonly(),
    city: z.string().min(1, { message: "City is required" }).readonly(),
    state: z.string().min(1, { message: "State is required" }).readonly(),
    zipCode: z.string().min(1, { message: "ZIP code is required" }).readonly(),
    country: z.string().min(1, { message: "Country is required" }).readonly(),
  })
  .strict();

// Base contact schema
const ContactSchema = z
  .object({
    phone: z
      .string()
      .min(1, { message: "Phone number is required" })
      .readonly(),
    email: z.email({ message: "Valid email is required" }).readonly(),
    website: z.url().optional().readonly(),
    additionalPhones: z.array(z.string()).optional().readonly(),
  })
  .strict();

// Operating hours schema
const DayHoursSchema = z
  .object({
    open: z.string().readonly(),
    close: z.string().readonly(),
    closed: z.boolean().readonly(),
  })
  .strict();

// Week start day enum
const WeekStartDaySchema = z.enum([
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
]);

// Fees & charges schemas
const GratuityChargeConfigSchema = z
  .object({
    enabled: z.boolean().readonly(),
    type: z.enum(["percentage", "flatAmount"]).readonly(),
    rate: z.number().min(0).max(1).optional().readonly(),
    amount: z.number().min(0).optional().readonly(),
    description: z.string().readonly(),
    applyToPartySize: z.number().int().min(0).optional().readonly(),
    taxable: z.boolean().optional().readonly(),
    taxRateIds: z.array(z.number().int()).optional().readonly(),
  })
  .strict();

const ServiceChargeConfigSchema = z
  .object({
    enabled: z.boolean().readonly(),
    type: z.enum(["percentage", "flatAmount"]).readonly(),
    rate: z.number().min(0).max(1).optional().readonly(),
    amount: z.number().min(0).optional().readonly(),
    description: z.string().readonly(),
    applyToPartySize: z.number().int().min(0).optional().readonly(),
  })
  .strict();

const DeliveryChargeConfigSchema = z
  .object({
    enabled: z.boolean().readonly(),
    type: z.enum(["flat", "percentage", "perDistance"]).readonly(),
    flatAmount: z.number().min(0).optional().readonly(),
    percentage: z.number().min(0).max(1).optional().readonly(),
    perDistanceRate: z.number().min(0).optional().readonly(),
    minimumCharge: z.number().min(0).optional().readonly(),
    maximumCharge: z.number().min(0).optional().readonly(),
    freeDeliveryThreshold: z.number().min(0).optional().readonly(),
    description: z.string().readonly(),
  })
  .strict();

const CustomFeeConfigSchema = z
  .object({
    id: z.string().readonly(),
    name: z.string().readonly(),
    enabled: z.boolean().readonly(),
    type: z.enum(["percentage", "flatAmount"]).readonly(),
    rate: z.number().min(0).max(1).optional().readonly(),
    amount: z.number().min(0).optional().readonly(),
    description: z.string().optional().readonly(),
    applyToOrderTypes: z.array(z.string()).optional().readonly(),
  })
  .strict();

export const OnlineServiceFeeConfigSchema = z
  .object({
    enabled: z.boolean().readonly(),
    amountCents: z.number().int().min(0).max(1000).readonly(),
    label: z.string().trim().min(1).max(40).readonly(),
    taxable: z.boolean().readonly(),
    taxRateIds: z.array(z.number().int()).readonly(),
  })
  .partial()
  .strict();

export const OnlineServiceFeeEnrollmentSchema = z
  .object({
    acknowledgedAt: z.string().min(1).readonly(),
    platformFeeCents: z.number().min(0).readonly(),
  })
  .strict(); // a uid (or anything else) in the public mirror is refused

const FeesAndChargesConfigSchema = z
  .object({
    gratuityCharge: GratuityChargeConfigSchema.optional().readonly(),
    serviceCharge: ServiceChargeConfigSchema.optional().readonly(),
    deliveryCharge: DeliveryChargeConfigSchema.optional().readonly(),
    customFees: z.array(CustomFeeConfigSchema).optional().readonly(),
    onlineServiceFee: OnlineServiceFeeConfigSchema.optional().readonly(),
  })
  .strict();

// Offline Payment Settings — see packages/models locations/Location `OfflinePaymentSettings`
// for the full design rationale. Bounds are intentionally narrow: a
// merchant should reason about offline payments in concrete terms ($50
// cap, 20-transaction queue, 24h horizon), not be able to type "$10000"
// by accident. Server-side validation enforces these on every PATCH so
// a forged request can't bypass the management UI.
//
// All amount limits are integer minor currency units (cents). Hours for
// age. The cross-field refinement (maxTotalDeferredCents >= maxAmountCents
// when enabled) is applied at the parent RestaurantSettingsSchema level
// because Zod refinements live on the schema that owns the related
// fields.
// Bounds are imported from @order-up/models so the Zod max() values
// stay in lockstep with the management UI's <input max={...}> values
// and the per-restaurant fraud-exposure analysis those limits embody.
// Drift between schema bounds and UI bounds is exactly the bug class
// an "industry level" architecture has to preclude.
const B = OFFLINE_PAYMENT_SETTINGS_BOUNDS;

// Format a cents value as `$1,234.56` for human-readable error
// messages. `toLocaleString` runs once at module load (these strings
// are baked into the schema's error metadata), so the runtime cost
// is zero.
const fmtUsd = (cents: number): string =>
  (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });

export const OfflinePaymentSettingsSchema = z
  .object({
    enabled: z.boolean().readonly(),
    maxAmountCents: z
      .number()
      .int({ message: "maxAmountCents must be an integer (cents)" })
      .min(0, { message: "maxAmountCents cannot be negative" })
      .max(B.maxAmountCentsCeiling, {
        message: `maxAmountCents cannot exceed ${fmtUsd(B.maxAmountCentsCeiling)}`,
      })
      .readonly(),
    maxQueueDepth: z
      .number()
      .int({ message: "maxQueueDepth must be an integer" })
      .min(1, { message: "maxQueueDepth must be at least 1" })
      .max(B.maxQueueDepthCeiling, {
        message: `maxQueueDepth cannot exceed ${B.maxQueueDepthCeiling}`,
      })
      .readonly(),
    maxTotalDeferredCents: z
      .number()
      .int({ message: "maxTotalDeferredCents must be an integer (cents)" })
      .min(0, { message: "maxTotalDeferredCents cannot be negative" })
      .max(B.maxTotalDeferredCentsCeiling, {
        message: `maxTotalDeferredCents cannot exceed ${fmtUsd(B.maxTotalDeferredCentsCeiling)}`,
      })
      .readonly(),
    maxAgeHours: z
      .number()
      .int({ message: "maxAgeHours must be an integer" })
      .min(1, { message: "maxAgeHours must be at least 1" })
      .max(B.maxAgeHoursCeiling, {
        message: `maxAgeHours cannot exceed ${B.maxAgeHoursCeiling}`,
      })
      .readonly(),
    // LOCATION-WIDE ceilings (optional — undefined = no location gate). See
    // OfflinePaymentSettings in @order-up/models and RiskPolicyService (POS).
    maxLocationTotalDeferredCents: z
      .number()
      .int({ message: "maxLocationTotalDeferredCents must be an integer (cents)" })
      .min(0, { message: "maxLocationTotalDeferredCents cannot be negative" })
      .max(B.maxLocationTotalDeferredCentsCeiling, {
        message: `maxLocationTotalDeferredCents cannot exceed ${fmtUsd(B.maxLocationTotalDeferredCentsCeiling)}`,
      })
      .optional()
      .readonly(),
    maxLocationPendingCount: z
      .number()
      .int({ message: "maxLocationPendingCount must be an integer" })
      .min(1, { message: "maxLocationPendingCount must be at least 1" })
      .max(B.maxLocationPendingCountCeiling, {
        message: `maxLocationPendingCount cannot exceed ${B.maxLocationPendingCountCeiling}`,
      })
      .optional()
      .readonly(),

    // Offline-capture cardholder consent disclosure. Arms the consent flow:
    // empty ⇒ recovery stays dark end-to-end (see OfflinePaymentSettings in
    // @order-up/models). Set by the merchant's risk/legal owner.
    consentDisclosureText: z
      .string()
      .max(5000, {
        message: "consentDisclosureText cannot exceed 5000 characters",
      })
      .optional()
      .readonly(),
    consentDisclosureVersion: z
      .string()
      .max(64, {
        message: "consentDisclosureVersion cannot exceed 64 characters",
      })
      .optional()
      .readonly(),
  })
  .strict();
export type OfflinePaymentSettingsType = z.infer<
  typeof OfflinePaymentSettingsSchema
>;

// Clock and date display format schemas
const ClockFormatSchema = z.enum(["12h", "24h"]);
const DateFormatPresetSchema = z.enum([
  "MM-DD-YYYY",
  "DD-MM-YYYY",
  "YYYY-MM-DD",
  "MM/DD/YYYY",
  "DD/MM/YYYY",
]);

// Scheduling / labor-compliance config — drives the ClockInMonitor
// lateness check and the ScheduleService weekly-hours guardrail.
export const SchedulingConfigSchema = z
  .object({
    lateThresholdMinutes: z
      .number()
      .int()
      .min(0)
      .max(240)
      .optional()
      .readonly(),
    maxWeeklyHoursPerEmployee: z
      .number()
      .int()
      .min(0)
      .max(168)
      .optional()
      .readonly(),
  })
  .strict();
export type SchedulingConfigType = z.infer<typeof SchedulingConfigSchema>;

// Receipt presentation — the restaurant-wide footer and tip ladder the
// renderers have always been able to draw and nothing ever supplied.
export const ReceiptPresentationSchema = z
  .object({
    footerLines: z.array(z.string().max(64)).max(6).default([]),
    // Whole percents only. A fractional tip option is a rounding argument at
    // the counter, and every processor's tip ladder is integral.
    tipPercents: z.array(z.number().int().min(1).max(100)).max(4).default([]),
  })
  .strict();
export type ReceiptPresentationType = z.infer<typeof ReceiptPresentationSchema>;

// Restaurant settings schema
// NOTE: Tax rates removed - these live in the `tax_rates` subcollection
// as they can vary by item type, location, order type, and require complex calculation logic
const LocationSettingsSchema = z
  .object({
    currency: z
      .string()
      .length(3, { message: "Currency must be a 3-letter ISO 4217 code" })
      .readonly(), // ISO 4217 (e.g., USD, EUR)
    allowTips: z.boolean().readonly(), // Whether tips are accepted
    // Card tip collection policy — see models CardTipCollectionMode.
    cardTipCollectionMode: z
      .enum(["none", "beforePayment", "afterAuth", "onReceipt"])
      .optional()
      .readonly(),
    // Tip auth buffer on two-step card holds — see models
    // LocationSettings.tipAuthBuffer. Absent = ON.
    tipAuthBuffer: z.boolean().optional().readonly(),
    // Tip-preset basis — see models TipBasis. Default 'discountedSubtotal'.
    tipBasis: z
      .enum(["discountedSubtotal", "subtotal", "total"])
      .optional()
      .readonly(),
    // RETIRED legacy boolean. Nothing reads it (the one acceptance setting is
    // `orderAcceptanceMode`) and nothing should write it; it stays declared,
    // optional, only so a `.strict()` parse of a settings map from an old
    // document does not throw `unrecognized_keys`.
    autoAcceptOrders: z.boolean().optional().readonly(),
    // The ONE order-acceptance setting for every incoming source — mirrors
    // models `OrderAcceptanceMode`. Optional: unset resolves to 'manual'.
    // MUST be declared: this schema is .strict() and the Restaurant model
    // materializes this key on every read, so leaving it out throws
    // `unrecognized_keys` on any settings write. See
    // docs/UNIFIED_ORDER_ACCEPTANCE_ARCHITECTURE.md §4.
    orderAcceptanceMode: z.enum(["auto", "manual"]).optional().readonly(),
    weekStartDay: WeekStartDaySchema.readonly(), // Which day the business week starts on
    timezone: z.string().min(1).max(64).optional().readonly(), // IANA business timezone (e.g. 'America/New_York'); falls back to tenant tz then 'UTC'
    clockFormat: ClockFormatSchema.optional().readonly(), // 12-hour (AM/PM) or 24-hour clock display
    dateFormat: DateFormatPresetSchema.optional().readonly(), // Date display format preference
    defaultLanguage: z.string().min(2).max(10).optional().readonly(), // Default POS UI language (BCP-47, e.g. 'en', 'es')
    showSeconds: z.boolean().optional().readonly(), // Show seconds in time displays
    takeoutGuestCount: z
      .union([z.literal(0), z.literal(1)])
      .optional()
      .readonly(), // Default guest count for non-dine-in orders
    // The employee (normally the Takeout station) that owns every delivery-app
    // order — see models LocationSettings.marketplaceOrderEmployeeId. Unset =
    // no owner. MUST be declared: this schema is .strict().
    marketplaceOrderEmployeeId: z.number().int().positive().optional().readonly(),
    batchOutTime: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/, {
        message: "Must be in HH:mm format (24-hour)",
      })
      .optional()
      .readonly(), // Daily auto-batch-out time
    autoClockOutTime: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/, {
        message: "Must be in HH:mm format (24-hour)",
      })
      .optional()
      .readonly(), // Daily auto-clock-out time
    lastAutoClockOutDate: z.string().optional().readonly(), // "YYYY-MM-DD" guard against re-processing
    lastAutoBatchCloseDate: z.string().optional().readonly(), // "YYYY-MM-DD" once-per-business-day guard for the batch-auto-close sweep
    lastAutoDrawerCloseDate: z.string().optional().readonly(), // "YYYY-MM-DD" once-per-business-day guard for the cash-drawer end-of-day sweep
    alerts: LocationAlertConfigSchema.optional().readonly(), // SMS alert configuration
    orderAlertRouting: OrderAlertRoutingConfigSchema.optional().readonly(), // New-order awareness routing
    remoteApproval: LocationRemoteApprovalConfigSchema.optional().readonly(), // Remote manager approval (who may approve from a phone)
    shiftAlerts: LocationShiftAlertsConfigSchema.optional().readonly(), // Shift reminders, lateness alerts and the on-time record
    missedPunchAlerts: z.object({ enabled: z.boolean().readonly() }).strict().optional().readonly(), // Manager alert after 16h clocked in; absent = off
    feesAndCharges: FeesAndChargesConfigSchema.optional().readonly(), // Service charges, delivery fees, custom fees
    offlinePayments: OfflinePaymentSettingsSchema.optional().readonly(), // Offline payment policy — opt-in per restaurant
    scheduling: SchedulingConfigSchema.optional().readonly(), // Scheduling / labor-compliance thresholds
    seatCount: z.number().optional().readonly(), // Total guest-facing seat count for RevPASH (matches model: just number, no constraint)
    enableSeatNumbering: z.boolean().optional().readonly(), // POS shows the seat-table diagram and per-seat ticket sections. Defaults to true on the model side; we keep it optional in the schema so existing restaurants don't need a migration.
    // Whether the POS captures a plain photo at clock-in/out for manager audit
    // (anti-buddy-punching). Opt-in per restaurant; undefined = off, so existing
    // restaurants need no migration. MUST be declared: this schema is .strict()
    // and is enforced on restaurant create + structured PATCH, so an undeclared
    // key throws `unrecognized_keys` and nothing persists.
    requireTimeClockPhoto: z.boolean().optional().readonly(),
    // Whether staff may clock in and out from the staff app (spec §14b, D20).
    // Absent = allowed. Server-written (`PUT /time-clock/settings` is the one
    // writer; the rules refuse every client), and declared here for the same
    // reason as the line above: .strict() refuses an undeclared key, so a
    // location carrying it would fail every whole-settings parse.
    allowStaffAppClockIn: z.boolean().optional().readonly(),
    // Whether sending a quick (no-table) order asks for the dining option and,
    // for takeout/delivery, the guest's phone + name before the kitchen gets it.
    // Opt-in per restaurant; undefined = off, so existing tills keep sending in
    // one tap. Declared for the same reason as the line above: .strict().
    promptTakeoutCustomerInfo: z.boolean().optional().readonly(),
    takeoutIntakeAllowSkip: z.boolean().optional().readonly(),
    // Last-mile courier dispatch provider (delivery-dispatch spine). The
    // DeliveryDispatchService reads this on order accept to decide which
    // provider's courier adapter to request a delivery from.
    //
    // These are ADAPTER keys (`DeliveryProviderKey`), not marketplaces — the
    // value is used to resolve a courier module from the delivery registry and
    // to look up an Integration by type. It mirrored `DeliveryPlatform` while
    // those were one union; KitchenHub split them, and this side is
    // unambiguously the adapter (docs/KITCHENHUB_API.md D1). Plus a `'none'`
    // sentinel for the default no-automatic-dispatch case.
    courierProvider: z
        .enum(["ubereats", "doordash", "grubhub", "kitchenhub", "uber_direct", "none"])
        .optional()
        .readonly(),
    // Whether OrderUp sends the customer courier-milestone SMS (confirmed /
    // picked-up / delivered). Opt-in (defaults to false) for TCPA/consent
    // safety. See `RestaurantSettings.customerCourierSmsEnabled`.
    customerCourierSmsEnabled: z.boolean().optional().readonly(),
    // STANDARD PREP TIME — the one restaurant-wide base every prep quote
    // starts from (`resolvePrepLeadMinutes`, 20 when absent). See
    // `RestaurantSettings.estimatedPrepTimeMinutes`.
    estimatedPrepTimeMinutes: z.number().int().min(0).optional().readonly(),
    // The one fulfilment autopilot's switch + windows for every order
    // (`RestaurantSettings.orderAutopilot`). Absent = on with defaults.
    orderAutopilot: z
      .object({
        enabled: z.boolean().optional(),
        readyGraceMinutes: z.number().min(0).optional(),
        pickupCompleteGraceMinutes: z.number().min(0).optional(),
        deliveryCompleteGraceMinutes: z.number().min(0).optional(),
      })
      .optional()
      .readonly(),
    // Dynamic prep time (docs/DYNAMIC_PREP_TIME_ARCHITECTURE.md) — always on;
    // see `RestaurantSettings.kitchenPacing`.
    kitchenPacing: z
      .object({
        standardItemMinutes: z.number().positive().max(240).optional(),
        loadWindowMinutes: z.number().positive().max(240).optional(),
        loadTiers: z
          .array(
            z.object({
              overItems: z.number().min(0).max(1000),
              addMinutes: z.number().min(0).max(240),
            }),
          )
          .max(10)
          .readonly()
          .optional(),
        largeOrder: z
          .object({
            overItems: z.number().min(0).max(1000),
            addMinutesPerItem: z.number().positive().max(60),
          })
          .optional(),
        maxPrepMinutes: z.number().positive().max(480).optional(),
        busyOverride: z
          .object({
            addMinutes: z.number().positive().max(240),
            until: z.coerce.date(),
          })
          .nullable()
          .optional(),
      })
      .optional()
      .readonly(),
    // Courier dropoff policy (operator-set, applied to every Drive delivery).
    // `courierContactlessDropoff` and `courierDropoffSignatureRequired` are
    // mutually exclusive on the DoorDash side; dispatch resolves the conflict
    // in favour of contactless. See `RestaurantSettings` JSDoc.
    courierContactlessDropoff: z.boolean().optional().readonly(),
    courierDropoffSignatureRequired: z.boolean().optional().readonly(),
    courierIdVerificationRequired: z.boolean().optional().readonly(),
    courierDropoffPhotoRequired: z.boolean().optional().readonly(),
    // Cash-tip loss-prevention policy (Stage 4). See
    // `RestaurantSettings.cashTipGovernance`. All money fields are INTEGER cents.
    cashTipGovernance: z
      .object({
        enabled: z.boolean(),
        hardGate: z.boolean().optional(),
        thresholdPercent: z.number().min(0).max(100).optional(),
        minCheckAmountCents: z.number().int().min(0).optional(),
        escalationTier1Seconds: z.number().int().min(0).optional(),
        escalationTier2Seconds: z.number().int().min(0).optional(),
        escalationTier3Seconds: z.number().int().min(0).optional(),
        autoCloseSeconds: z.number().int().min(0).optional(),
        escalationPhones: z.array(z.string()).optional().readonly(),
      })
      .strict()
      .optional()
      .readonly(),
    // How a location records and pays out cash tips. See
    // `LocationSettings.cashTips`. SPARSE, no defaults.
    cashTips: z
      .object({
        recording: z.enum(['perCheck', 'declaredOnly']).optional(),
        payout: z.enum(['sameDay', 'payroll']).optional(),
        poolDeclared: z.boolean().optional(),
        recordTipOutAtClockOut: z.boolean().optional(),
      })
      .strict()
      .optional()
      .readonly(),
    // Card holds (pre-auth card on file) for tabs and table service. See
    // `LocationSettings.cardHolds`. SPARSE, no defaults, server-written; money
    // is INTEGER cents. Dark behind `initiative.card_holds`.
    cardHolds: z
      .object({
        enabled: z.boolean().optional(),
        tabMode: z.boolean().optional(),
        tableService: z.boolean().optional(),
        holdAmountCents: z
          .number()
          .int()
          .min(CARD_HOLD_AMOUNT_MIN_CENTS)
          .max(CARD_HOLD_AMOUNT_MAX_CENTS)
          .optional(),
        requireCardForTab: z.boolean().optional(),
        walkoutGratuityPercent: z
          .number()
          .int()
          .min(CARD_HOLD_WALKOUT_GRATUITY_MIN_PERCENT)
          .max(CARD_HOLD_WALKOUT_GRATUITY_MAX_PERCENT)
          .optional(),
      })
      .strict()
      .optional()
      .readonly(),
    // Guest-facing wifi printed on QR codes (NOT the infrastructure
    // NetworkConfig SSID/PSK). See `RestaurantSettings.guestWifi` JSDoc.
    guestWifi: GuestWifiSettingsSchema.optional().readonly(),
    // Guest table page, service requests, and (later pieces) pay-at-table and
    // tabs. See `RestaurantSettings.guestOrdering` JSDoc.
    guestOrdering: GuestOrderingSettingsSchema.optional().readonly(),
    // Restaurant-wide printing settings.
    // SPARSE, no defaults — see `RestaurantSettings.printing` JSDoc.
    printing: LocationPrintingSettingsSchema.optional().readonly(),
    // Appointments booking policy. SPARSE,
    // no defaults, server-written — see `RestaurantSettings.booking`.
    booking: UpdateBookingSettingsSchema.extend({
      reminderOffsetsMinutes: z.array(z.number().int().nonnegative()).readonly().optional(),
    })
      .strict()
      .optional()
      .readonly(),
    // Per-restaurant gift-card overrides.
    // SPARSE, no defaults, server-written — see `RestaurantSettings.giftCards`.
    giftCards: z
      .object({
        // The gift-card program this location belongs to; absent = none.
        programId: z.string().min(1).optional(),
        acceptGiftCards: z.boolean().optional(),
        canIssueGiftCards: z.boolean().optional(),
        canReloadGiftCards: z.boolean().optional(),
        canVoidGiftCardTransactions: z.boolean().optional(),
        canRefundGiftCards: z.boolean().optional(),
        denominationPresetsOverride: z.array(z.number().int().positive()).readonly().optional(),
      })
      .strict()
      .optional()
      .readonly(),
    // Bar spirit lists (formerly bar_settings/spirit_lists). SPARSE, no
    // defaults, server-written — see `RestaurantSettings.barSpiritLists`.
    barSpiritLists: LocationBarSpiritListsBlockSchema.optional().readonly(),
    // Bar wine list pours. SPARSE, server-written — see `LocationSettings.barWineList`.
    barWineList: LocationBarWineListBlockSchema.optional().readonly(),
    // The owner's Online Service Fee acknowledgment. SERVER-WRITTEN only -
    // see `LocationSettings.onlineServiceFeeEnrollment`.
    onlineServiceFeeEnrollment: OnlineServiceFeeEnrollmentSchema.optional().readonly(),
  })
  .strict()
  .superRefine((data, ctx) => {
    // The offline-payments total ceiling must accommodate at least one
    // max-amount transaction; otherwise the policy is internally
    // inconsistent (no offline capture would ever be admitted). Skipped
    // when offlinePayments is omitted or disabled.
    const op = data.offlinePayments;
    if (op && op.enabled && op.maxTotalDeferredCents < op.maxAmountCents) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "maxTotalDeferredCents must be at least maxAmountCents (otherwise no offline capture would be admitted)",
        path: ["offlinePayments", "maxTotalDeferredCents"],
      });
    }
  });

// ========================================
// Google Business Profile Integration Schemas
// These enable OrderUp to be the source of truth
// ========================================

// Service options schema - maps to Google Business Profile service types
const LocationServiceOptionsSchema = z
  .object({
    dineIn: z.boolean().readonly(),
    takeout: z.boolean().readonly(),
    delivery: z.boolean().readonly(),
    curbsidePickup: z.boolean().readonly(),
    noContactDelivery: z.boolean().readonly(),
    driveThrough: z.boolean().optional().readonly(),
    outdoorDining: z.boolean().optional().readonly(),
  })
  .strict();

// Parking type enum
const ParkingTypeSchema = z.enum([
  "free",
  "paid",
  "street",
  "valet",
  "garage",
  "none",
]);

// Restaurant attributes schema - comprehensive amenities and features
const LocationAttributesSchema = z
  .object({
    // Accessibility
    wheelchairAccessibleEntrance: z.boolean().optional().readonly(),
    wheelchairAccessibleSeating: z.boolean().optional().readonly(),
    wheelchairAccessibleRestroom: z.boolean().optional().readonly(),

    // Dining Options
    outdoorSeating: z.boolean().optional().readonly(),
    rooftopSeating: z.boolean().optional().readonly(),
    privateRooms: z.boolean().optional().readonly(),
    patio: z.boolean().optional().readonly(),
    fireplace: z.boolean().optional().readonly(),

    // Amenities
    wifi: z.boolean().optional().readonly(),
    parking: ParkingTypeSchema.optional().readonly(),
    valetParking: z.boolean().optional().readonly(),
    bikeParking: z.boolean().optional().readonly(),
    evCharging: z.boolean().optional().readonly(),

    // Reservations & Planning
    reservationsRequired: z.boolean().optional().readonly(),
    reservationsAccepted: z.boolean().optional().readonly(),
    onlineReservations: z.boolean().optional().readonly(),
    // External booking URL (OpenTable, Resy, Tock, …) for the website's
    // "Reserve Online" button; the button hides when absent.
    reservationUrl: z.url().optional().readonly(),
    waitlistAvailable: z.boolean().optional().readonly(),

    // Payment Methods
    acceptsCreditCards: z.boolean().optional().readonly(),
    acceptsDebitCards: z.boolean().optional().readonly(),
    acceptsCash: z.boolean().optional().readonly(),
    acceptsNfc: z.boolean().optional().readonly(),
    acceptsCrypto: z.boolean().optional().readonly(),

    // Dietary & Menu Features
    vegetarianOptions: z.boolean().optional().readonly(),
    veganOptions: z.boolean().optional().readonly(),
    glutenFreeOptions: z.boolean().optional().readonly(),
    halalOptions: z.boolean().optional().readonly(),
    kosherOptions: z.boolean().optional().readonly(),
    organicOptions: z.boolean().optional().readonly(),

    // Alcohol
    servesAlcohol: z.boolean().optional().readonly(),
    fullBar: z.boolean().optional().readonly(),
    beerAndWine: z.boolean().optional().readonly(),
    byob: z.boolean().optional().readonly(),
    happyHour: z.boolean().optional().readonly(),

    // Entertainment & Atmosphere
    liveMusic: z.boolean().optional().readonly(),
    liveEntertainment: z.boolean().optional().readonly(),
    sportsTv: z.boolean().optional().readonly(),
    danceFloor: z.boolean().optional().readonly(),
    karaoke: z.boolean().optional().readonly(),

    // Family & Groups
    familyFriendly: z.boolean().optional().readonly(),
    kidsMenu: z.boolean().optional().readonly(),
    highChairs: z.boolean().optional().readonly(),
    goodForGroups: z.boolean().optional().readonly(),
    privateEvents: z.boolean().optional().readonly(),

    // Services
    catering: z.boolean().optional().readonly(),
    brunch: z.boolean().optional().readonly(),
    breakfast: z.boolean().optional().readonly(),
    lunch: z.boolean().optional().readonly(),
    dinner: z.boolean().optional().readonly(),
    lateNight: z.boolean().optional().readonly(),

    // Health & Safety
    sanitized: z.boolean().optional().readonly(),
    staffWearsMasks: z.boolean().optional().readonly(),
    temperatureChecks: z.boolean().optional().readonly(),
    contactlessPayment: z.boolean().optional().readonly(),
  })
  .strict();

// Special hours schema - for holidays, events, temporary changes
const SpecialHoursSchema = z
  .object({
    date: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, {
        message: "Date must be in YYYY-MM-DD format",
      })
      .readonly(),
    openTime: z
      .string()
      .regex(/^\d{2}:\d{2}$/, { message: "Time must be in HH:MM format" })
      .optional()
      .readonly(),
    closeTime: z
      .string()
      .regex(/^\d{2}:\d{2}$/, { message: "Time must be in HH:MM format" })
      .optional()
      .readonly(),
    isClosed: z.boolean().readonly(),
    reason: z.string().max(100).optional().readonly(),
  })
  .strict();

// Restaurant location/coordinates schema
const RestaurantLocationSchema = z
  .object({
    latitude: z.number().min(-90).max(90).readonly(),
    longitude: z.number().min(-180).max(180).readonly(),
    placeId: z.string().optional().readonly(),
  })
  .strict();

// Platform sync configuration schema. The object is kept separate from the
// strict+transform wrapper so GoogleBusinessSyncConfigSchema can extend it.
const PlatformSyncConfigObject = z
  .object({
    enabled: z.boolean().readonly(),
    connectionId: z.string().optional().readonly(), // UUID-based connection identifier
    accountId: z.string().optional().readonly(),
    businessId: z.string().optional().readonly(),
    pageId: z.string().optional().readonly(),
    // The platform's own location id. Shared by all seven platforms, so it is
    // NOT Google-specific (restaurant → location rename, Phase 0 expand).
    platformLocationId: z.string().optional().readonly(),
    /** @deprecated legacy stored name (Phase 0 expand) — fills platformLocationId when absent. */
    locationId: z.string().optional().readonly(), // location-word-ok: legacy name (Phase 0 contract)
    accessToken: z.string().optional().readonly(), // OAuth access token
    refreshToken: z.string().optional().readonly(), // OAuth refresh token
    tokenExpiresAt: z.coerce.date().optional().readonly(), // Token expiration date - coerce handles string input
    syncStatus: z
      .enum(["idle", "syncing", "error", "completed", "connected"])
      .optional()
      .readonly(),
    aiConfig: z
      .object({
        enabled: z.boolean().readonly(),
        responseStyle: z.string().optional().readonly(),
        autoReply: z.boolean().optional().readonly(),
        responseLength: z
          .enum(["short", "medium", "long"])
          .optional()
          .readonly(),
        includeBusinessName: z.boolean().optional().readonly(),
        minimumRating: z.number().min(1).max(5).optional().readonly(),
        maxResponseTime: z.number().positive().optional().readonly(),
        businessVoice: z.string().optional().readonly(),
      })
      .optional()
      .readonly(),
    lastSync: z.coerce.date().optional().readonly(), // Coerce to handle string input
    lastError: z.string().optional().readonly(),
  });

/**
 * Phase 0 expand: a doc written before the rename carries only the legacy
 * name; fill platformLocationId from it. Spread CONDITIONALLY so a config with
 * neither name gains no explicitly-undefined key (a caller that turns
 * `undefined` into a field delete would otherwise wipe it).
 */
function withPlatformLocationId<T extends { platformLocationId?: string; locationId?: string }>(v: T): T { // location-word-ok: legacy name (Phase 0 contract)
  const platformLocationId = v.platformLocationId ?? v.locationId; // location-word-ok: legacy name (Phase 0 contract)
  return platformLocationId === undefined ? v : { ...v, platformLocationId };
}

const PlatformSyncConfigSchema = PlatformSyncConfigObject.strict().transform(withPlatformLocationId);

/**
 * Google Places API schemas removed - migrated to Google Business Profile API
 * All Google Places functionality has been replaced with Google Business Profile integration
 */

// Google Business Sync Config Schema (imports from integration schema to avoid circular deps)
// Note: We define a simplified version here if we can't import the full one easily,
// but ideally we should organize schemas to allow imports.
// For now, extending PlatformSyncConfigSchema to include specific GB fields if needed.
const GoogleBusinessSyncConfigSchema = PlatformSyncConfigObject.extend({
  aiConfig: z
    .object({
      enabled: z.boolean().readonly(),
      responseStyle: z.string().optional().readonly(),
      autoReply: z.boolean().optional().readonly(),
      responseLength: z.enum(["short", "medium", "long"]).optional().readonly(),
      includeBusinessName: z.boolean().optional().readonly(),
      minimumRating: z.number().min(1).max(5).optional().readonly(),
      maxResponseTime: z.number().positive().optional().readonly(),
      businessVoice: z.string().optional().readonly(),
    })
    .optional()
    .readonly(),
}).strict().transform(withPlatformLocationId);

// Restaurant platform sync schema
const LocationPlatformSyncSchema = z
  .object({
    enabled: z.boolean().readonly(),
    autoSync: z.boolean().readonly(),
    syncFrequency: z.enum(["hourly", "daily", "weekly"]).readonly(),
    platforms: z
      .object({
        googleMyBusiness: GoogleBusinessSyncConfigSchema.optional().readonly(),
        yelp: PlatformSyncConfigSchema.optional().readonly(),
        facebook: PlatformSyncConfigSchema.optional().readonly(),
        instagram: PlatformSyncConfigSchema.optional().readonly(),
        appleMaps: PlatformSyncConfigSchema.optional().readonly(),
        doordash: PlatformSyncConfigSchema.optional().readonly(),
        ubereats: PlatformSyncConfigSchema.optional().readonly(),
      })
      .strict()
      .readonly(),
  })
  .strict();

// Image field validators — `...Base` is the optional `string | OptimizedImage`
// union (branding fields); `imageFieldSchema` is the required variant
// (GalleryPhoto.image). Both mirror the model's `string | OptimizedImage`.
import { imageFieldSchema, imageFieldValidatorBase } from "../images/OptimizedImage";
import {
  HERO_SLIDES_MAX,
  isSafeFontUrl,
  sanitizeFontFamilyName,
  WEBSITE_LAYOUT_TEMPLATES,
  WEBSITE_TEMPLATE_STYLES,
} from "@order-up/models";

/**
 * A date field tolerant of every shape a Firestore timestamp field takes when a
 * whole restaurant doc is Zod-parsed:
 *   - a JS `Date` (already converted)
 *   - an ISO / parseable date string
 *   - a Firestore `Timestamp` (Admin or client SDK) via its `.toDate()` method,
 *     or its serialized `{ seconds, nanoseconds }` / `{ _seconds, _nanoseconds }`
 *     JSON form.
 * `z.coerce.date()` alone turns a Timestamp OBJECT into `Invalid Date`; this
 * preprocess normalizes to a `Date` first so a stray Timestamp can never poison
 * a whole-doc parse.
 */
const firestoreTimestampTolerantDate = z.preprocess((value) => {
  if (value && typeof value === "object") {
    const ts = value as {
      toDate?: () => Date;
      seconds?: number;
      nanoseconds?: number;
      _seconds?: number;
      _nanoseconds?: number;
    };
    if (typeof ts.toDate === "function") return ts.toDate();
    const seconds = ts.seconds ?? ts._seconds;
    const nanoseconds = ts.nanoseconds ?? ts._nanoseconds ?? 0;
    if (typeof seconds === "number") {
      return new Date(seconds * 1000 + Math.floor(nanoseconds / 1_000_000));
    }
  }
  return value;
}, z.coerce.date());

// Per-location Google review auto-reply settings (voice card is AI-written, owner-editable).
const ReviewVoiceCardSchema = z.object({
  greeting: z.string(),
  signOff: z.string(),
  length: z.enum(["short", "medium", "long"]),
  emoji: z.boolean(),
  complaintStyle: z.string(),
  alwaysSay: z.array(z.string()),
  neverSay: z.array(z.string()),
  defaultLanguage: z.string(),
  editedByOwner: z.boolean(),
  generatedAt: firestoreTimestampTolerantDate,
});

const LocationReviewAutoReplySchema = z.object({
  enabled: z.boolean().optional(),
  activatedAt: firestoreTimestampTolerantDate.optional(),
  voiceCard: ReviewVoiceCardSchema.optional(),
});

// Website layout template + style schemas — consume the canonical const arrays
// from @order-up/models (single source of truth) so the Zod enum can never
// drift from the TS union or the API `_shared.ts` fragments.
const WebsiteLayoutTemplateSchema = z.enum(WEBSITE_LAYOUT_TEMPLATES);
const WebsiteTemplateStyleSchema = z.enum(WEBSITE_TEMPLATE_STYLES);

// Restaurant branding schema
// One owner-selected font slot: a curated registry key or an uploaded custom
// font (family name + woff2/ttf URL). URL required only for custom fonts.
const LocationFontChoiceSchema = z
  .object({
    source: z.enum(["curated", "custom"]).readonly(),
    family: z.string().min(1).max(120).readonly(),
    url: z.string().url().optional().readonly(),
  })
  .strict()
  .refine((f) => f.source !== "custom" || !!f.url, {
    message: "A custom font requires an uploaded font URL.",
    path: ["url"],
  })
  // Mirror the render-time guards (shared @order-up/models): a stored family
  // must contain no CSS-breaking characters and any URL must be a bare https
  // URL — so a hostile value can never be stored and later injected.
  .refine((f) => sanitizeFontFamilyName(f.family) === f.family, {
    message: "Font family contains invalid characters.",
    path: ["family"],
  })
  .refine((f) => !f.url || isSafeFontUrl(f.url), {
    message: "A custom font URL must be a plain https:// link.",
    path: ["url"],
  });

// Owner-authored Spotlight card (dine-in Style 2). The button URL is held to
// http(s) like every other owner link on the site.
const LocationSpotlightSchema = z
  .object({
    heading: z.string().max(80).optional().readonly(),
    body: z.string().max(400).optional().readonly(),
    buttonLabel: z.string().max(40).optional().readonly(),
    buttonUrl: z
      .string()
      .regex(/^https?:\/\/\S+$/i, "Enter a full URL (e.g. https://example.com)")
      .optional()
      .readonly(),
    image: imageFieldValidatorBase.readonly(),
  })
  .strict();

const LocationBrandingSchema = z
  .object({
    logo: imageFieldValidatorBase.readonly(), // Full logo for header use
    logoMode: z.enum(["image", "text"]).optional().readonly(),
    heroDisplay: z.enum(["headline", "logo"]).optional().readonly(), // Hero heading: text headline (default) or logo image
    heroWelcomeText: z.string().optional().readonly(), // Hero "Welcome to" eyebrow message (dine-in Style 1); default "Welcome to"
    heroWelcomeSize: z.enum(["sm", "md", "lg", "xl"]).optional().readonly(), // Hero "Welcome to" eyebrow size preset (dine-in Style 1); default "xl"
    icon: imageFieldValidatorBase.readonly(), // Icon/favicon with circle avatar styling
    heroImage: imageFieldValidatorBase.readonly(),
    heroImageOpacity: z.number().min(0).max(100).optional().readonly(),
    bannerImage: imageFieldValidatorBase.optional().readonly(), // Banner image for page headers
    backgroundImage: imageFieldValidatorBase.optional().readonly(), // Background texture/pattern
    menuPreviewImage: imageFieldValidatorBase.optional().readonly(), // Dedicated menu-preview section photo
    storyImage: imageFieldValidatorBase.optional().readonly(), // Dedicated "Our Story" split-band photo (dine-in Style 1)
    featureImage: imageFieldValidatorBase.optional().readonly(), // Standalone full-width photo band below the story section (dine-in Style 1)
    supplementaryImages: z.array(imageFieldSchema).optional().readonly(), // Niche/supplementary images (elements required, matching the model array)
    heroSlides: z.array(imageFieldSchema).max(HERO_SLIDES_MAX).optional().readonly(), // Ordered hero slideshow (dine-in Style 2); absent = heroImage + gallery
    spotlight: LocationSpotlightSchema.optional().readonly(), // Owner override for the dine-in Style-2 Spotlight card
    useBrandColors: z.boolean().optional().readonly(), // Dine-in Style 2: let brand colors onto buttons/rules (default monochrome)
    fonts: z
      .object({
        heading: LocationFontChoiceSchema.optional().readonly(),
        body: LocationFontChoiceSchema.optional().readonly(),
      })
      .optional()
      .readonly(), // Owner-selected heading/body fonts (curated key or uploaded custom font)
    colors: z
      .object({
        primary: z
          .string()
          .regex(/^#[0-9a-f]{6}$/i, "Must be a valid hex color")
          .readonly(),
        secondary: z
          .string()
          .regex(/^#[0-9a-f]{6}$/i, "Must be a valid hex color")
          .readonly(),
        accent: z
          .string()
          .regex(/^#[0-9a-f]{6}$/i, "Must be a valid hex color")
          .readonly(),
        primaryOpacity: z.number().min(0).max(100).optional().readonly(),
        secondaryOpacity: z.number().min(0).max(100).optional().readonly(),
        accentOpacity: z.number().min(0).max(100).optional().readonly(),
        buttonColor: z
          .string()
          .regex(/^#[0-9a-f]{6}$/i, "Must be a valid hex color")
          .optional()
          .readonly(),
        buttonOpacity: z.number().min(0).max(100).optional().readonly(),
        // Owner intent: true once any brand color was saved in the builder;
        // families stop substituting tuned default palettes. Absent = legacy.
        customized: z.boolean().optional().readonly(),
      })
      .strict()
      .readonly(),
  })
  .strict();

// Price range symbol (standard $ symbols based on average entrée price)
const PriceRangeSymbolSchema = z.enum(["$", "$$", "$$$", "$$$$"]);

// Restaurant price metrics calculated from menu items
const LocationPriceMetricsSchema = z
  .object({
    priceRange: PriceRangeSymbolSchema.readonly(),
    averagePrice: z.number().nonnegative().readonly(),
    medianPrice: z.number().nonnegative().readonly(),
    minPrice: z.number().nonnegative().readonly(),
    maxPrice: z.number().nonnegative().readonly(),
    itemCount: z.number().int().nonnegative().readonly(),
    // Tolerant of Firestore Timestamp | string | Date (see helper docblock).
    calculatedAt: firestoreTimestampTolerantDate.readonly(),
  })
  .strict();

// Restaurant SEO schema
const LocationSEOSchema = z
  .object({
    title: z.string().max(60, "Title should be under 60 characters").readonly(),
    description: z
      .string()
      .max(160, "Description should be under 160 characters")
      .readonly(),
    keywords: z.array(z.string()).readonly(),
    ogImage: z.url().optional().readonly(),
    priceRange: PriceRangeSymbolSchema.optional().readonly(), // Manually set or calculated
    priceMetrics: LocationPriceMetricsSchema.optional().readonly(), // Calculated from menu
    // Which generated page families this site publishes. Absent = published.
    generatedPages: z
      .object({
        menuItems: z.boolean().optional().readonly(),
        tags: z.boolean().optional().readonly(),
        places: z.boolean().optional().readonly(),
      })
      .strict()
      .optional()
      .readonly(),
    verification: z
      .object({
        google: z.string().optional().readonly(), // Google Search Console
        bing: z.string().optional().readonly(), // Bing Webmaster
        yandex: z.string().optional().readonly(), // Yandex Webmaster
        pinterest: z.string().optional().readonly(), // Pinterest domain claim
      })
      .optional()
      .readonly(),
  })
  .strict();

// Restaurant content schema
const LocationContentSchema = z
  .object({
    aboutText: z.string().readonly(),
    specialOffers: z.array(z.string()).readonly(),
    announcement: z.string().optional().readonly(),
  })
  .strict();

// Restaurant social media schema
const LocationSocialSchema = z
  .object({
    facebook: z.url().optional().readonly(),
    instagram: z.url().optional().readonly(),
    twitter: z.url().optional().readonly(),
    tiktok: z.url().optional().readonly(),
  })
  .strict();

// Restaurant ordering configuration schema (business rules for ordering)
const LocationOrderingConfigSchema = z
  .object({
    minLeadTimeMinutes: z.number().min(0).max(1440).default(15).readonly(), // 0-24 hours
    timeSlotIntervalMinutes: z.number().min(5).max(120).default(15).readonly(), // 5min-2hrs
    closingBufferMinutes: z.number().min(0).max(180).default(15).readonly(), // 0-3 hours
    enableAsapOrders: z.boolean().default(true).readonly(),
    // enableDelivery/enablePickup: builder-written, but fulfillment gating is
    // canonically the top-level Restaurant.deliveryEnabled/onlineOrderingEnabled
    // (SEO JSON-LD now derives from those). Kept until the builder toggle retires.
    enableDelivery: z.boolean().default(false).readonly(),
    enablePickup: z.boolean().default(true).readonly(),
  })
  .strict();

// Restaurant ordering integration schema
const LocationOrderingIntegrationSchema = z
  .object({
    enabled: z.boolean().readonly(),
    provider: z
      .enum(["internal", "doordash", "ubereats", "grubhub", "custom"])
      .readonly(),
    url: z.url().optional().readonly(),
    buttonText: z.string().readonly(),
    buttonColor: z
      .string()
      .regex(/^#[0-9a-f]{6}$/i, "Must be a valid hex color")
      .readonly(),
    config: LocationOrderingConfigSchema.optional().readonly(),
  })
  .strict();

// Saved per-restaurant menu-import house style (deterministic name composition).
const NamingPolicySchema = z
  .object({
    primary: z.enum(["native", "english"]),
    parenthetical: z.enum(["none", "native", "english"]),
    posNameFrom: z.enum(["primary", "native", "english", "none"]),
    kitchenNameFrom: z.enum(["primary", "native", "english", "none"]),
  })
  .strict();

const HouseStyleProfileSchema = z
  .object({ naming: NamingPolicySchema })
  .strict();

// Restaurant content block schema
const LocationContentBlockSchema = z
  .object({
    id: z
      .string()
      .min(1, { message: "Content block ID is required" })
      .readonly(),
    title: z
      .string()
      .min(1, { message: "Content block title is required" })
      .readonly(),
    description: z
      .string()
      .min(1, { message: "Content block description is required" })
      .readonly(),
    image: z.string().optional().readonly(), // URL string or OptimizedImage JSON
    icon: z.string().optional().readonly(), // Icon name (e.g., 'Heart', 'ChefHat')
    layout: z
      .enum(["image-left", "image-right", "centered", "text-only"])
      .readonly(),
    backgroundColor: z.string().optional().readonly(),
    textColor: z.string().optional().readonly(),
    instanceId: z.string().optional().readonly(), // Instance identifier for multiple content block sections
  })
  .strict();

// Restaurant CTA section schema
const CtaImageShapeSchema = z.enum([
  "none",
  "circle",
  "house",
  "hexagon",
  "diamond",
  "arch",
  "shield",
  "leaf",
  "octagon",
  "star",
  "cross",
  "drop",
]);

const LocationCtaSectionSchema = z
  .object({
    id: z.string().min(1, { message: "CTA section ID is required" }).readonly(),
    title: z
      .string()
      .min(1, { message: "CTA section title is required" })
      .readonly(),
    description: z
      .string()
      .min(1, { message: "CTA section description is required" })
      .readonly(),
    backgroundImage: z.string().optional().readonly(), // URL string or OptimizedImage JSON
    instanceId: z.string().optional().readonly(), // Instance identifier for multiple CTA sections
    imageShape: CtaImageShapeSchema.optional().readonly(), // Decorative clip-path shape for CTA image
  })
  .strict();

// Owner-created custom page schema. A page is a titled, ordered collection of
// the SAME content blocks used elsewhere; `slug` is the URL segment and is
// validated against the shared slug contract (format + reserved-collision) that
// `@order-up/models` also drives in the builder — so the two can never drift.
const CustomPageSlugSchema = z
  .string()
  .max(CUSTOM_PAGE_SLUG_MAX_LENGTH)
  .superRefine((slug, ctx) => {
    const issue = validateCustomPageSlug(slug);
    if (!issue) return;
    const message =
      issue === "reserved"
        ? "Page URL is a reserved route (menu, gallery, contact, story, and asset paths are built in)"
        : issue === "format"
          ? "Page URL must be lowercase letters, numbers, and single hyphens"
          : issue === "too-long"
            ? `Page URL must be ${CUSTOM_PAGE_SLUG_MAX_LENGTH} characters or fewer`
            : "Page URL is required";
    ctx.addIssue({ code: z.ZodIssueCode.custom, message });
  });

const LocationCustomPageSchema = z
  .object({
    id: z.string().min(1, { message: "Custom page ID is required" }).readonly(),
    slug: CustomPageSlugSchema.readonly(),
    title: z
      .string()
      .min(1, { message: "Custom page title is required" })
      .readonly(),
    enabled: z.boolean().readonly(),
    navLabel: z.string().optional().readonly(),
    showInNav: z.boolean().optional().readonly(),
    blocks: z.array(LocationContentBlockSchema).readonly(),
    seo: z
      .object({
        title: z.string().optional().readonly(),
        description: z.string().optional().readonly(),
      })
      .strict()
      .optional()
      .readonly(),
  })
  .strict();

// The custom-pages array — enforces slug UNIQUENESS across the collection (the
// per-item schema only enforces per-slug shape + reserved-collision). Exported
// so the builder's atomic-write field validator reuses the exact same rule.
const LocationCustomPagesSchema = z
  .array(LocationCustomPageSchema)
  .superRefine((pages, ctx) => {
    const dupes = findDuplicateCustomPageSlugs(pages.map((p) => p.slug));
    if (dupes.size === 0) return;
    pages.forEach((page, index) => {
      if (dupes.has((page.slug ?? "").toLowerCase())) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Duplicate page URL "${page.slug}" — each page needs a unique URL`,
          path: [index, "slug"],
        });
      }
    });
  });

// Process step schema (e.g. "How It Works")
const LocationProcessStepSchema = z
  .object({
    id: z.string().min(1).readonly(),
    title: z.string().min(1).readonly(),
    description: z.string().min(1).readonly(),
    icon: z.string().optional().readonly(),
    image: z.string().optional().readonly(),
  })
  .strict();

// Service feature schema (e.g. "Our Features")
const LocationServiceFeatureSchema = z
  .object({
    id: z.string().min(1).readonly(),
    title: z.string().min(1).readonly(),
    description: z.string().min(1).readonly(),
    icon: z.string().optional().readonly(),
  })
  .strict();

// Website menu section schema - menu group reference with visibility toggle
const WebsiteMenuSectionSchema = z
  .object({
    menuGroupId: z.number().readonly(),
    name: z.string().readonly(),
    visible: z.boolean().readonly(),
    position: z.number().readonly(),
  })
  .strict();

// Gallery photo schema - individual photo with visibility and ordering
const GalleryPhotoSchema = z
  .object({
    id: z.string().readonly(),
    // Required to match the model's `GalleryPhoto.image: string | OptimizedImage`.
    image: imageFieldSchema.readonly(),
    caption: z.string().optional().readonly(),
    visible: z.boolean().readonly(),
    position: z.number().readonly(),
  })
  .strict();

// Manually-curated Instagram post — mirrors GalleryPhoto plus a post `link`.
const InstagramManualPostSchema = z
  .object({
    id: z.string().readonly(),
    image: imageFieldSchema.readonly(),
    caption: z.string().optional().readonly(),
    link: z.string().url().optional().readonly(),
    visible: z.boolean().readonly(),
    position: z.number().readonly(),
  })
  .strict();

// Website-facing Instagram feed config (section on/off + how it's sourced).
// The OAuth connection/token + auto-synced posts live elsewhere (integrations
// collection + instagram_feed subcollection); this only holds builder-edited state.
const InstagramFeedConfigSchema = z
  .object({
    enabled: z.boolean().optional().readonly(),
    mode: z.enum(["auto", "manual"]).optional().readonly(),
    displayCount: z.number().int().min(3).max(12).optional().readonly(),
    manualPosts: z.array(InstagramManualPostSchema).optional().readonly(),
    headingEyebrow: z.string().optional().readonly(),
  })
  .strict();

// Restaurant website configuration schema
const LocationWebsiteConfigSchema = z
  .object({
    enabled: z.boolean().readonly(),
    parentMenuGroupId: z.number().positive().optional().readonly(), // Auto-select this menu for customer-facing sites
    layoutTemplate: WebsiteLayoutTemplateSchema.optional().readonly(), // Selected website layout template
    templateStyle: WebsiteTemplateStyleSchema.optional().readonly(), // Visual style variant within template category (default '1')
    // Provenance written by the hosting select-template route; mirrors the
    // SQL hosting record so Firestore and the mirror stay in parity.
    templateKey: z.string().optional().readonly(),
    lastEditedBy: z.string().optional().readonly(),
    // Written as FieldValue.serverTimestamp() by the hosting select-template
    // route → lands as a Firestore Timestamp. Tolerant of Timestamp | string |
    // Date so a whole-doc parse never yields Invalid Date.
    lastEditedAt: firestoreTimestampTolerantDate.optional().readonly(),
    branding: LocationBrandingSchema.readonly(),
    seo: LocationSEOSchema.readonly(),
    content: LocationContentSchema.readonly(),
    contentBlocks: z.array(LocationContentBlockSchema).optional().readonly(), // Configurable content blocks
    ctaSections: z.array(LocationCtaSectionSchema).optional().readonly(), // Configurable CTA sections
    customPages: LocationCustomPagesSchema.optional().readonly(), // Owner-created standalone pages (slug + blocks)
    processSteps: z.array(LocationProcessStepSchema).optional().readonly(), // Configurable process steps
    processStepsTitle: z.string().optional().readonly(),
    serviceFeatures: z
      .array(LocationServiceFeatureSchema)
      .optional()
      .readonly(), // Configurable service features
    serviceFeaturesTitle: z.string().optional().readonly(),
    serviceFeaturesSubtitle: z.string().optional().readonly(),
    galleryEnabled: z.boolean().optional(), // Show/hide gallery section on website (default true)
    // Show/hide the newsletter signup section (default false; multi-location
    // treats ABSENT as enabled for legacy parity — see the model docblock).
    newsletterEnabled: z.boolean().optional().readonly(),
    galleryImages: z.array(imageFieldSchema).optional().readonly(), // Legacy: raw image array (elements required, matching the model array)
    galleryPhotos: z.array(GalleryPhotoSchema).optional().readonly(), // Gallery photos with per-photo controls
    menuSections: z.array(WebsiteMenuSectionSchema).optional().readonly(), // Menu groups with visibility toggles
    contactForm: z
      .object({
        enabled: z.boolean().readonly(),
        heading: z.string().optional().readonly(),
        subheading: z.string().optional().readonly(),
        successMessage: z.string().optional().readonly(),
      })
      .strict()
      .optional()
      .readonly(), // Contact form configuration
    // Curated featured menu items for the marketing site. `menuItemGuids` are
    // Item.guid values (in display order); `blurbs` is an optional per-item
    // marketing description keyed by the same Item.guid.
    featuredItems: z
      .object({
        menuItemGuids: z.array(z.string()).readonly(),
        source: z.enum(["manual", "product-mix", "ai"]).readonly(),
        blurbs: z.record(z.string(), z.string()).optional().readonly(),
        generatedAt: z.string().optional().readonly(),
      })
      .strict()
      .optional()
      .readonly(),
    social: LocationSocialSchema.readonly(),
    ordering: LocationOrderingIntegrationSchema.readonly(),
    // Per-restaurant web-analytics / marketing-pixel IDs injected into the
    // public site <head> (GA4 / Meta Pixel / GTM).
    analytics: z
      .object({
        ga4MeasurementId: z.string().optional().readonly(),
        metaPixelId: z.string().optional().readonly(),
        gtmId: z.string().optional().readonly(),
      })
      .strict()
      .optional()
      .readonly(),
    // First-party Instagram feed section config (section on/off, mode, curated posts).
    instagramFeed: InstagramFeedConfigSchema.optional().readonly(),
  })
  .strict();

// Google Business Profile Schema (simplified for embedding in Restaurant)
const GoogleBusinessProfileEmbeddedSchema = z
  .object({
    googleLocationId: z.string().optional().readonly(),
    /** @deprecated legacy stored name (Phase 0 expand) — filled into googleLocationId when absent. */
    locationId: z.string().optional().readonly(), // location-word-ok: legacy name (Phase 0 contract)
    name: z.string().readonly(),
    description: z.string().optional().readonly(),
    address: z
      .object({
        street: z.string().readonly(),
        city: z.string().readonly(),
        state: z.string().readonly(),
        postalCode: z.string().readonly(),
        country: z.string().readonly(),
        formatted: z.string().readonly(),
      })
      .readonly(),
    phone: z.string().readonly(),
    additionalPhones: z.array(z.string()).optional().readonly(),
    website: z.string().optional().readonly(),
    // Google's own write-a-review link, mirrored rather than constructed.
    newReviewUri: z.string().optional().readonly(),
    placeId: z.string().optional().readonly(),
    // Neighbourhoods/towns served, mirrored from the GBP service area. The
    // authoritative copy sits beside live OAuth tokens on the integration doc.
    serviceAreas: z
      .array(
        z.object({
          name: z.string().readonly(),
          placeId: z.string().readonly(),
        }),
      )
      .optional()
      .readonly(),
    coordinates: z
      .object({
        latitude: z.number().readonly(),
        longitude: z.number().readonly(),
      })
      .optional()
      .readonly(), // Optional - may not be available from Google API
    hours: z
      .object({
        regularHours: z
          .array(
            z.object({
              openDay: z.string().readonly(),
              openTime: z.string().readonly(),
              closeDay: z.string().readonly(),
              closeTime: z.string().readonly(),
            }),
          )
          .readonly(),
        specialHours: z
          .array(
            z.object({
              startDate: z.coerce.date().readonly(),
              openTime: z.string().readonly(),
              closeTime: z.string().readonly(),
              isClosed: z.boolean().readonly(),
            }),
          )
          .optional()
          .readonly(),
      })
      .readonly(),
    rating: z.number().optional().readonly(),
    reviewCount: z.number().optional().readonly(),
    responseRate: z.number().optional().readonly(),
    profileCompleteness: z.number().optional().readonly(),
    missingFields: z.array(z.string()).optional().readonly(),
    categories: z.array(z.string()).readonly(),
    verified: z.boolean().readonly(),
    mapsUri: z.string().readonly(),
    lastSynced: z.coerce.date().readonly(),
  })
  .strict()
  .transform((v, ctx) => {
    const googleLocationId = v.googleLocationId ?? v.locationId; // location-word-ok: legacy name (Phase 0 contract)
    if (googleLocationId === undefined) {
      ctx.addIssue({ code: "custom", message: "googleLocationId is required", path: ["googleLocationId"] });
      return z.NEVER;
    }
    return { ...v, googleLocationId };
  });

const LocationFeaturesSchema = z.object({
  website: z.boolean().default(true),
  giftCards: z.boolean().default(true),
  advertising: z.boolean().default(true),
  orderUpBooks: z.boolean().default(true),
  payroll: z.boolean().default(true),
  posEnabled: z.boolean().default(true),
  // Phase 14 — RecipeOps inventory + recipe costing add-on. Defaults false
  // so existing restaurants stay opt-in; the POS order-close flow only
  // calls `book_decrement_inventory_for_order` when this flag is true.
  recipeOps: z.boolean().default(false),
  // Firm Portal ("Client Management" in admin) — the cross-tenant Clients
  // workspace. Defaults false so existing restaurants stay opt-in; the
  // financial layout seeds it true via `defaultFeaturesForBusinessType`.
  firmPortal: z.boolean().default(false),
  // OrderUp Drive paid-storage add-on mirror. Optional: the drive is always
  // available at the free 25 GB tier, so this only marks "has a paid plan".
  // Written by RestaurantFeatureFlagSyncService when a `location_drive` add-on
  // is active; kept here so a synced doc round-trips without dropping the key.
  locationDrive: z.boolean().optional(),
  // ⚠️ A KEY MISSING FROM THIS OBJECT IS SILENTLY DELETED, NOT IGNORED.
  // `z.object` strips unknown keys, `updateRestaurant` runs the whole patch
  // through `RestaurantSchema.partial().parse(...)`, and `features` is written
  // to Firestore as a WHOLE MAP — so a flag with no entry here is dropped from
  // the doc by the next unrelated edit. `appointments` had no entry from the
  // day it shipped: `Restaurant`'s constructor hydrates and preserves it, and
  // the parse threw it away again on every admin save. Every flag on
  // `RestaurantFeatures` must appear here.
  appointments: z.boolean().optional(),
  // Property Management vertical (PM-0). Optional and default-absent for the
  // same reason as the two above: a doc that predates the flag stays locked
  // (the section's FeatureGatePage passes `defaultEnabled={false}`).
  // OrderUp Studio — the design canvas. ADDED HERE because the flag reached
  // `RestaurantFeatures` and `RESTAURANT_FEATURE_TO_FEATURE_KEY` without
  // reaching this schema, so `RestaurantSchema` stripped it on every parse
  // and an admin enabling Design Studio had it silently discarded — the
  // exact failure the comment above describes. The round-trip test in
  // __tests__/Restaurant.test.ts is what caught it.
  designStudio: z.boolean().optional(),
  propertyManagement: z.boolean().optional(),
  // Automatic review requests. Optional and default-absent: absent means the
  // restaurant never opted in, which is not the same as opting out, and either
  // way nothing is sent. Declared here because the comment above is not
  // advisory — a flag missing from this object is stripped on the next
  // unrelated save, and `appointments` and `designStudio` both lived that way.
  reviewRequests: z.boolean().optional(),
  // Text alerts (SMS) unlock — staff-set only. Declared here for the same
  // reason as every flag above: a key missing from this object is stripped on
  // the next save, which would silently re-lock an unlocked location.
  textAlerts: z.boolean().optional(),
  // Remote manager approval — a paid add-on, written by the entitlement
  // projector or a site admin. Missing here, the next save would strip it and
  // re-lock a location that is paying for it.
  remoteManagerApproval: z.boolean().optional(),
  scheduling: z.boolean().optional(),
  // Customer loyalty — a subscription toggle written by the entitlement
  // projector or a site admin. Missing here, the next save would strip it and
  // switch loyalty off at a location that has it.
  customerLoyalty: z.boolean().optional(),
});

// Per-restaurant TRX gateway credentials. See `TrxCredentials` in
// `@order-up/models` for shape rationale and the deferred KMS plan.
// `.strict()` rejects unknown keys so a typo (`payPagGuid`) fails loudly
// at validation rather than silently being persisted as dead data.
//
// Required fields (TRX issues these at merchant onboarding):
//   clientId, sourceId, password
//
// Optional fields:
//   payPageGuid — required only for hosted-tokenization web checkout;
//                 admins fill in once a PayPage is configured in the
//                 TRX portal.
//   key, iv     — legacy slots kept for backward compatibility with
//                 documents that already carry them. No current runtime
//                 path consumes them.
const TrxCredentialsSchema = z
  .object({
    clientId: z.string().min(1, { message: "TRX clientId is required" }).readonly(),
    sourceId: z.string().min(1, { message: "TRX sourceId is required" }).readonly(),
    // The password transits here only as WRITE INPUT (admin provisioning /
    // rotation). At rest it lives ONLY in the sealed payment_credentials
    // vault (encrypted, KMS-primary) — the restaurant doc persists the
    // non-secret `trxCredentialsPublic` projection instead.
    password: z.string().min(1, { message: "TRX password is required" }).readonly(),
    payPageGuid: z.string().min(1).readonly().optional(),
    key: z.string().min(1).readonly().optional(),
    iv: z.string().min(1).readonly().optional(),
  })
  .strict();

// Non-secret projection of the TRX credentials, PERSISTED on the restaurant
// doc (the secret itself lives in the sealed vault). `clientId`/`sourceId` are
// TRX-issued identifiers, `payPageGuid` is embedded in the hosted-checkout URL,
// and `hasPassword` is the presence flag privileged UI + validators use in
// place of the password. Mirrors `TrxCredentialsPublic` in @order-up/models.
const TrxCredentialsPublicSchema = z
  .object({
    clientId: z.string().readonly(),
    sourceId: z.string().readonly(),
    payPageGuid: z.string().min(1).readonly().optional(),
    hasPassword: z.boolean().readonly(),
  })
  .strict();

// Per-rail merchant capability — what the processor underwrote and HQ activated
// at go-live. Non-secret, platform-written, PERSISTED on the restaurant doc.
// Mirrors `MerchantApprovedRails` in @order-up/models; `RestaurantSchema` is
// `.strict()`, so this declaration is what stops an `approvedRails`-carrying
// update payload from failing with `unrecognized_keys` and 400-ing every
// restaurant save.
//
// Both flags are REQUIRED booleans (no `.default()`, no `.optional()`): the
// whole map is optional, but a map that exists must state BOTH rails
// explicitly. A defaulted flag would let a partial write silently assert a
// capability nobody underwrote — the fail-closed rule lives in the model's
// `toMerchantApprovedRails`/`isRailApproved`, and the schema must not undercut
// it. NOT routing (`providerRouting.providerByRail`) and not the merchant's
// questionnaire intent (`MerchantExpectedActivity`).
const MerchantApprovedRailsSchema = z
  .object({
    card: z.boolean().readonly(),
    ach: z.boolean().readonly(),
  })
  .strict();

// Main Restaurant schema - matches our model exactly
export const GuestCardCaptureSchema = z
  .object({
    onlineCheckout: z.enum(['hold', 'sale']).optional(),
    payAtTable: z.enum(['hold', 'sale']).optional(),
    payLink: z.literal('sale').optional(),
  })
  .strict();

export const MerchantAccountRoutingSchema = z
  .object({
    cardPresent: z.string().min(1).optional(),
    cardNotPresent: z.string().min(1).optional(),
    storedCardsShared: z.boolean().optional(),
  })
  .strict();

const LocationSchema = z
  .object({
    // Core Identifiers
    restaurantId: z.number().positive().readonly(),
    guid: z.string().readonly(),
    slug: z.string(), // Mutable
    tenantId: z.number().positive().readonly(),
    tenantGuid: z.string().optional().readonly(),

    // Payment Processing — platform-controlled (admin app writes,
    // tenants do not). Routing + credentials co-located so a single
    // doc read returns the full payment configuration.
    providerRouting: PaymentRoutingSettingsSchema.optional().readonly(),
    // Whether payments move REAL money. Absent ⇒ live (see DEFAULT_PAYMENT_MODE);
    // no `.default()`, because stamping "live" onto every restaurant that omits
    // it would rewrite the whole estate on its next save for no behaviour change.
    paymentMode: PaymentModeSchema.optional().readonly(),
    // How each guest card surface takes a card (hold vs sale). STAFF-written
    // only (rules `cardPaymentFieldsUntouched`). Absent => sale.
    guestCardCapture: GuestCardCaptureSchema.optional().readonly(),
    // Optimistic-concurrency counter for the staff card-payments route.
    cardPaymentsVersion: z.number().int().nonnegative().optional().readonly(),
    // Channel → merchant account (MID). STAFF-written only. Absent ⇒ primary.
    merchantAccountRouting: MerchantAccountRoutingSchema.optional().readonly(),
    trxMerchantAccountIds: z.array(z.string().min(1)).readonly().optional().readonly(),
    trxCredentials: TrxCredentialsSchema.optional().readonly(),
    trxCredentialsPublic: TrxCredentialsPublicSchema.optional().readonly(),
    // Underwriting/activation capability per rail. Optional: a restaurant that
    // has not been activated carries NO map at all (absent ≠ approved).
    approvedRails: MerchantApprovedRailsSchema.optional().readonly(),

    // Basic Business Information
    name: z
      .string()
      .min(1, { message: "Restaurant name is required" })
      .readonly(),
    displayName: z
      .string()
      .min(1, { message: "Display name must not be empty" })
      .max(150, { message: "Display name must be 150 characters or less" })
      .optional()
      .readonly(),
    description: z
      .string()
      .max(750, { message: "Description must be 750 characters or less" })
      .optional()
      .readonly(),
    concept: z.string().readonly(),
    businessTypeGuid: z.string().optional().readonly(),
    categories: z.array(z.string()).optional().readonly(),
    openingDate: z.coerce.date().optional().readonly(), // Coerce to handle string input

    // Location & Contact
    address: AddressSchema.readonly(),
    contact: ContactSchema.readonly(),
    receiptPresentation: ReceiptPresentationSchema.optional().readonly(),
    coordinates: RestaurantLocationSchema.optional().readonly(),

    // Operating Hours.
    // `hoursMode` is optional for backward compat with docs written before
    // the field existed (Restaurant model defaults missing values to
    // `'fixed'`). New writes always set it.
    hoursMode: z.enum(["fixed", "by_appointment"]).optional().readonly(),
    operatingHours: z.record(z.string(), DayHoursSchema).readonly(),
    specialHours: z.array(SpecialHoursSchema).optional().readonly(),
    // Online ordering + marketplace hours when they differ from the business
    // hours above; `null` clears them back to "same as business hours".
    onlineOrderingHours: z.record(z.string(), DayHoursSchema).nullable().optional().readonly(),

    // Online Ordering & Delivery — managed from the Takeout & Delivery surface.
    // These mirror the IRestaurant fields (Restaurant.ts:1058-1086). The schema
    // is `.strict()`, so without these declarations every Takeout & Delivery save
    // threw `unrecognized_keys` (Zod v4) at RestaurantSchema.partial().parse and
    // nothing persisted. NOT `.readonly()` — these are user-editable settings.
    onlineOrderingEnabled: z.boolean().optional(),
    // Time-bound first-party pause (New-Order Awareness auto-pause). Coerced so a
    // Firestore Timestamp/ISO string round-trips to a Date. See IRestaurant.
    onlineOrderingPausedUntil: z.coerce.date().optional(),
    deliveryEnabled: z.boolean().optional(),
    acceptOrdersUntilClosing: z.boolean().optional(),
    thirdPartyOrdering: z
      .record(z.string(), z.enum(["on", "off"]))
      .optional(),
    packagingPreferences: z
      .object({
        includeNapkins: z.boolean().optional(),
        includeUtensils: z.boolean().optional(),
        includeCondiments: z.boolean().optional(),
        customItems: z.array(z.string()).optional(),
        notes: z.string().optional(),
      })
      .optional(),

    // Service & Dining Options
    serviceOptions: LocationServiceOptionsSchema.optional().readonly(),
    attributes: LocationAttributesSchema.optional().readonly(),

    // Status & Settings
    status: z.enum(["active", "inactive"]).readonly(),
    settings: LocationSettingsSchema.readonly(),

    // Platform Integrations
    platformSync: LocationPlatformSyncSchema.optional().readonly(),
    googleBusinessProfile:
      GoogleBusinessProfileEmbeddedSchema.optional().readonly(),

    // Website Configuration
    websiteConfig: LocationWebsiteConfigSchema.optional().readonly(),
    // Owner's work-in-progress website draft. Same shape as the live config;
    // the management builder edits it and the owner-preview path renders it,
    // while the public renderer/publish gate read `websiteConfig` only.
    // Optional so docs without a draft (the default) validate unchanged.
    websiteConfigDraft: LocationWebsiteConfigSchema.optional().readonly(),

    // POS Configuration
    posConfig: PosConfigSchema.optional().readonly(),

    // Saved menu-import naming house style
    menuImportHouseStyle: HouseStyleProfileSchema.optional().readonly(),

    // Feature Flags
    features: LocationFeaturesSchema.optional().readonly(),
    reviewAutoReply: LocationReviewAutoReplySchema.optional().readonly(),

    // Timestamps
    createdAt: z.date().readonly(),
    updatedAt: z.date().readonly(),
  })
  .strict();

// Schema for creating a new restaurant
const CreateLocationSchema = z.object({
  // Core
  tenantId: z
    .number()
    .positive()
    .min(1, { message: "Valid tenant ID is required" }),

  // Payment Processing
  providerRouting: PaymentRoutingSettingsSchema.optional(),
  paymentMode: PaymentModeSchema.optional(),
  guestCardCapture: GuestCardCaptureSchema.optional(),
  cardPaymentsVersion: z.number().int().nonnegative().optional(),
  merchantAccountRouting: MerchantAccountRoutingSchema.optional(),
  trxMerchantAccountIds: z.array(z.string().min(1)).optional(),
  trxCredentials: TrxCredentialsSchema.optional(),

  // Basic Business Information
  name: z.string().min(1, { message: "Restaurant name is required" }),
  displayName: z
    .string()
    .min(1, { message: "Display name must not be empty" })
    .max(150, { message: "Display name must be 150 characters or less" })
    .optional(),
  description: z
    .string()
    .max(750, { message: "Description must be 750 characters or less" })
    .optional(),
  // Optional on create. The UI conditionally requires it (only when the
  // selected business type exposes sub-categories — see
  // apps/admin/src/components/admin/RestaurantModal.tsx). The model
  // defaults missing concept to "Restaurant" in the constructor, so an
  // omitted value here is safe.
  concept: z
    .string()
    .min(1, { message: "Restaurant concept must not be empty when provided" })
    .optional(),
  businessTypeGuid: z.string().optional(),
  categories: z.array(z.string()).optional(),
  openingDate: z.coerce.date().optional(),

  // Location & Contact
  address: AddressSchema,
  contact: ContactSchema,
  receiptPresentation: ReceiptPresentationSchema.optional(),
  coordinates: RestaurantLocationSchema.optional(),

  // Operating Hours.
  // `hoursMode` optional on create — Restaurant model defaults missing
  // values to `'fixed'`. The onboarding wizard always sets it; admin
  // RestaurantModal sets it via the Hours-tab top-level toggle.
  hoursMode: z.enum(["fixed", "by_appointment"]).optional(),
  operatingHours: z.record(z.string(), DayHoursSchema),
  specialHours: z.array(SpecialHoursSchema).optional(),
  onlineOrderingHours: z.record(z.string(), DayHoursSchema).nullable().optional(),

  // Service & Dining Options
  serviceOptions: LocationServiceOptionsSchema.optional(),
  attributes: LocationAttributesSchema.partial().optional(),

  // Settings & Integrations
  settings: LocationSettingsSchema,
  platformSync: LocationPlatformSyncSchema.optional(),
  websiteConfig: LocationWebsiteConfigSchema.optional(),
  features: LocationFeaturesSchema.optional(),
  // reviewAutoReply is server-owned (LocationAutoReplyStore); never accepted on create.
});

// Schema for updating restaurant status
const UpdateLocationStatusSchema = z.object({
  tenantId: z
    .number()
    .positive()
    .min(10000001, { message: "Valid tenant ID is required" }),
  restaurantId: z.string().min(1, { message: "Restaurant ID is required" }),
  status: z.enum(["active", "inactive"]),
});

// Schema for updating restaurant details
const UpdateLocationSchema = LocationSchema.partial()
  .omit({
    restaurantId: true,
    guid: true,
    tenantId: true,
    createdAt: true,
    updatedAt: true,
    // Server-owned: written only by the API's LocationAutoReplyStore.
    reviewAutoReply: true,
  })
  .extend({
    restaurantId: z
      .number()
      .positive({ message: "Restaurant ID must be a positive number" }),
  });

// Export types for TypeScript usage
export type LocationType = z.infer<typeof LocationSchema>;
export type CreateLocationType = z.infer<typeof CreateLocationSchema>;
export type UpdateLocationStatusType = z.infer<
  typeof UpdateLocationStatusSchema
>;
export type UpdateLocationType = z.infer<typeof UpdateLocationSchema>;
export type AddressType = z.infer<typeof AddressSchema>;
export type ContactType = z.infer<typeof ContactSchema>;
export type DayHoursType = z.infer<typeof DayHoursSchema>;
export type LocationSettingsType = z.infer<typeof LocationSettingsSchema>;
export type PlatformSyncConfigType = z.infer<typeof PlatformSyncConfigSchema>;
export type LocationPlatformSyncType = z.infer<
  typeof LocationPlatformSyncSchema
>;
export type LocationBrandingType = z.infer<typeof LocationBrandingSchema>;
export type LocationSEOType = z.infer<typeof LocationSEOSchema>;
export type LocationContentType = z.infer<typeof LocationContentSchema>;
export type LocationContentBlockType = z.infer<
  typeof LocationContentBlockSchema
>;
export type LocationCtaSectionType = z.infer<
  typeof LocationCtaSectionSchema
>;
export type LocationCustomPageType = z.infer<
  typeof LocationCustomPageSchema
>;
export type LocationSocialType = z.infer<typeof LocationSocialSchema>;
export type LocationOrderingConfigType = z.infer<
  typeof LocationOrderingConfigSchema
>;
export type LocationOrderingIntegrationType = z.infer<
  typeof LocationOrderingIntegrationSchema
>;
export type LocationWebsiteConfigType = z.infer<
  typeof LocationWebsiteConfigSchema
>;
export type WebsiteLayoutTemplateType = z.infer<
  typeof WebsiteLayoutTemplateSchema
>;
export type WebsiteTemplateStyleType = z.infer<
  typeof WebsiteTemplateStyleSchema
>;
export type WebsiteMenuSectionType = z.infer<typeof WebsiteMenuSectionSchema>;
export type GalleryPhotoType = z.infer<typeof GalleryPhotoSchema>;
export type InstagramManualPostType = z.infer<typeof InstagramManualPostSchema>;
export type InstagramFeedConfigType = z.infer<typeof InstagramFeedConfigSchema>;

// New Google Business integration types
export type LocationServiceOptionsType = z.infer<
  typeof LocationServiceOptionsSchema
>;
export type LocationAttributesType = z.infer<
  typeof LocationAttributesSchema
>;
export type SpecialHoursType = z.infer<typeof SpecialHoursSchema>;
export type RestaurantLocationType = z.infer<typeof RestaurantLocationSchema>;
export type ParkingTypeSchemaType = z.infer<typeof ParkingTypeSchema>;

export default LocationSchema;
export {
  AddressSchema,
  ContactSchema,
  CreateLocationSchema,
  DayHoursSchema,
  GalleryPhotoSchema,
  InstagramFeedConfigSchema,
  InstagramManualPostSchema,
  MerchantApprovedRailsSchema,
  ParkingTypeSchema,
  PlatformSyncConfigSchema,
  LocationAttributesSchema,
  LocationBrandingSchema,
  LocationContentBlockSchema,
  LocationContentSchema,
  LocationCtaSectionSchema,
  LocationCustomPageSchema,
  LocationCustomPagesSchema,
  RestaurantLocationSchema,
  LocationOrderingConfigSchema,
  LocationOrderingIntegrationSchema,
  LocationPlatformSyncSchema,
  LocationProcessStepSchema,
  LocationSEOSchema,
  LocationServiceFeatureSchema,
  LocationServiceOptionsSchema,
  LocationSettingsSchema,
  LocationSocialSchema,
  LocationWebsiteConfigSchema,
  SpecialHoursSchema,
  TrxCredentialsPublicSchema,
  TrxCredentialsSchema,
  UpdateLocationSchema,
  UpdateLocationStatusSchema,
  WebsiteLayoutTemplateSchema,
  WebsiteMenuSectionSchema,
  WebsiteTemplateStyleSchema,
};
